import { motion } from "framer-motion";
import { ScanLine, Download as DownloadIcon, Lock } from "lucide-react";
import { DOWNLOAD_URL } from "./Download";

/**
 * Replaces the old FPSCalculator section (GPU/CPU/RAM dropdowns computing a personal "before/after"
 * FPS from a hand-authored lookup table with no measurement behind it — see the Phase 1 design
 * discussion for why that had to go). This points at the real thing instead: the app's Free Scan
 * feature actually benchmarks the visitor's own PC. The panel below is a labeled example of what
 * the app shows, not a live calculation for this visitor — the whole point is we stopped
 * pretending to know someone's FPS without measuring it.
 */
export const FreeScanPromo = () => {
  return (
    <section id="free-scan" className="relative py-24 sm:py-32 overflow-hidden" data-testid="free-scan-promo-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4 slash">
              Free Scan
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-6"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Stop guessing.
              <br />
              <span className="text-orange-500">Scan it.</span>
            </h2>
            <p className="text-zinc-400 text-base max-w-lg leading-relaxed mb-8">
              Don't take a GPU chart's word for it. Download Flow Tweaks and run a free scan — a
              real ~25 second benchmark measures <span className="text-white">this exact PC's</span>{" "}
              baseline FPS, then shows what's available to fix and roughly what systems like yours
              typically gain. No purchase, no account, no guesswork.
            </p>
            <a
              href={DOWNLOAD_URL}
              className="glass-hover inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-full transition-all hover:shadow-[0_0_30px_rgba(255,94,0,0.5)]"
              data-testid="free-scan-download-cta"
            >
              <DownloadIcon className="w-5 h-5" />
              Download &amp; Run Free Scan
            </a>
            <p className="mt-4 text-[11px] text-zinc-500 uppercase tracking-[0.15em]">
              Free · No account needed · Windows
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.7 }}
            className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-8"
            data-testid="free-scan-example-panel"
          >
            <div className="flex items-center gap-2 mb-5">
              <ScanLine className="w-4 h-4 text-orange-500" />
              <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                Example scan result — not your PC
              </p>
            </div>

            <div className="flex gap-8 font-mono mb-6">
              <div>
                <p className="text-3xl font-black text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  142
                </p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide">fps average</p>
              </div>
              <div>
                <p className="text-3xl font-black text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  89
                </p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide">fps 1% low</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-5">
              <p className="text-lg font-black text-white mb-1">31 optimizations available</p>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Systems with similar specs typically reach a higher FPS range after optimization —
                based on comparable hardware, not a guarantee.
              </p>
            </div>

            <div className="mt-5 flex items-start gap-2 text-xs text-zinc-500">
              <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Every tweak's name and benefit are shown for free — unlocking a plan applies them.
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
